import React, { useEffect, useRef, useState } from "react";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GridColDef } from "@mui/x-data-grid";
import EditIcon from '../../assets/images/icons/EditIcon.svg';
import "../Dynamic/UserList.scss";
import { CommonService } from "../../Service/CommonService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import StatusDropdown from "../Common/StatusDropdown";
import ExpandMore from "../../assets/images/icons/ExpandMore.svg";
import { Form } from "react-bootstrap";
import Constant from "../../Constant";
import CompanyContractCapacityListing from "./CompanyContractCapacityListing";
import CapacityDetailsChart from "./CapacityDetailChart";
import DataGridLoader from "../Common/DataGridLoader";

const CompaniesContractListing: React.FC = () => {
  const history = useHistory();
  const [companiesContract, setCompaniesContract] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const buttonRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const [sortField, setSortField] = useState("createdDate"); // Default sort
  const [sortDirection, setSortDirection] = useState("desc"); // Default ascending
  const [filter, setFilter] = useState<any>({
    companyName: null,
    contractId: null,
    contractType: null,
    startDate: null,
    endDate: null,
    termMonths: null,
    typeOfContainer: null,
    initialQuantity: null,
    status: null,
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const statusOptions = [
    { value: "ACTIVE", label: "Active" },
    { value: "EXPIRED", label: "Expired" },
  ];
  const [isContractOverviewClicked, setContractOverviewClicked] = useState(true);
  const [isCapacityInsightsClicked, setCapacityInsightsClicked] = useState(false);
  const [isCapacityTabActive, setCapacityTabActive] = useState(false);

  const handleAddContract = () => {
    history.push("/master/add-contract");
  };

  //formate the status
  function formatStatus(status: string): string {
    const formattedStatus = status
      .split(/[_\\]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return formattedStatus;
  }
  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      const newSortField = sortModel[0].field;
      const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

      setSortField(newSortField);
      setSortDirection(newSortDirection);

      fetchAllCompaniesContracts(
        page,
        pageSize,
        newSortField,
        newSortDirection,
        filter
      );
    }
  };

  const fetchAllCompaniesContracts = async (
    page: any,
    size: any,
    sortField: any,
    sortDirection: any,
    filter: any
  ) => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setLoading(true);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      const response = await InventoryApiService.fetchAllCompaniesContracts(
        page,
        size,
        sortField,
        sortDirection,
        filter,
        CommonService.isAdmin() ? null : CommonService.getCompanyId(),
        signal
      );
      setCompaniesContract(response.data.content);
      setTotalCount(response.data.totalElements);
    } catch (error) {
      console.log("error occurred while api calling " + error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCompaniesContracts(
      page,
      pageSize,
      sortField,
      sortDirection,
      filter
    );
  }, []);

  const handleBack = () => {
    history.push("/master/dashboard");
  };

  const handleStatusFilterChange = (
    event: any,
    value: string,
    status: string | null
  ) => {
    const isChecked = event.target.checked;

    // Convert the status string to an array
    const statusArray = status ? status.split(",") : [];

    // Update the status list based on checkbox state
    const updatedStatusArray = isChecked
      ? [...statusArray, value] // Add status if checked
      : statusArray.filter((s) => s !== value); // Remove status if unchecked

    // Convert array back to a comma-separated string
    const updatedStatus =
      updatedStatusArray?.length > 0 ? updatedStatusArray.join(",") : null;

    // Update the filter state with the modified status string
    setFilter({ ...filter, status: updatedStatus });

    //call api
    fetchAllCompaniesContracts(page, pageSize, sortField, sortDirection, {
      ...filter,
      status: updatedStatus,
    });
  };

  const getStatusOptions = () => {
    return statusOptions;
  };

  const handleRowClick = (params: any) => {
    const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
    history.push(`${linkPrefix}/view-contract/${params.row.id}`);
  };

  //add tooltip
  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString()}>
        <span>{value?.toString()}</span>
      </Tooltip>
    );
  };

  const handleEditContract = (id:any) => {
    history.push('/master/edit-contract/' + id);
  }

  const handleCapacityInsightTabClick = () => {
    setContractOverviewClicked(false);
    setCapacityTabActive(true);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        divRef.current &&
        !divRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]); // Only run when dropdown is toggled

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof any
  ) => {
    const value = event.target.value;

    // Prepare a temporary filter object with the updated field value
    const tempFilter = {
      ...filter, // Spread existing filter state
      [field]: field === "status" && value === "" ? null : value, // Update field only if the condition is met
    };

    // Update the filter state
    setFilter(tempFilter);

    //call api
    fetchAllCompaniesContracts(
      page,
      pageSize,
      sortField,
      sortDirection,
      tempFilter
    );
  };

  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  const columns: GridColDef[] = [
    ...(CommonService.isAdmin()
      ? [
          {
            field: "companyName",
            headerName: "Company",
            flex: 1,
            minWidth: 130,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
              <div>
                <Typography
                  variant="body2"
                  sx={{ color: "#6F6F6F", fontSize: "14px" }}
                >
                  {params.colDef.headerName}
                </Typography>
                <div className="position-relative" style={{ width: "120px" }}>
                  <input
                    type="text"
                    className="form-control border mt-1"
                    style={{ textIndent: "24px", boxShadow: "none" }}
                    placeholder="Search"
                    value={filter.companyName}
                    defaultValue={filter.companyName}
                    onChange={(event) =>
                      handleFilterChange(event, "companyName")
                    }
                    onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                  />
                  <SearchIcon
                    sx={{
                      color: "#6F6F6F",
                      position: "absolute",
                      top: "50%",
                      left: "8px",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            ),
            renderCell: (params: any) => (
              <CellWithTooltip value={params.value || "-"} />
            ),
          },
        ]
      : []),
    {
      field: "contractId",
      headerName: "Contract ID",
      flex: 1,
      minWidth: 130,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "120px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.contractId}
              defaultValue={filter.contractId}
              onChange={(event) => handleFilterChange(event, "contractId")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "contractType",
      headerName: "Type",
      minWidth: 120,
      flex: 1,
      sortable: false, // disables sorting for this column
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 100 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.contractType}
              defaultValue={filter.contractType}
              onChange={(event) => handleFilterChange(event, "contractType")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      minWidth: 130,
      flex: 1,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "120px" }}>
            <input
              type="date"
              className="form-control border mt-1"
              style={{ boxShadow: "none" }}
              id="birthdate"
              name="modifiedDate"
              placeholder="MM/DD/YYYY"
              value={filter.startDate}
              defaultValue={filter.startDate}
              onChange={(event) => handleFilterChange(event, "startDate")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip
          value={CommonService.formatDateToReadableString(params.value) || "-"}
        />
      ),
    },
    {
      field: "endDate",
      headerName: "End Date",
      minWidth: 130,
      flex: 1,
      headerClassName: "table-header border-0 my-2 h-100",

      renderCell: (params: any) => (
        <CellWithTooltip
          value={CommonService.formatDateToReadableString(params.value) || "-"}
        />
      ),
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "120px" }}>
            <input
              type="date"
              className="form-control border mt-1"
              style={{ boxShadow: "none" }}
              id="endDate"
              name="endDate"
              placeholder="MM/DD/YYYY"
              value={filter.endDate}
              defaultValue={filter.endDate}
              onChange={(event) => handleFilterChange(event, "endDate")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
          </div>
        </div>
      ),
    },
    {
      field: "termMonths",
      headerName: "Term",
      minWidth: 130,
      flex: 1,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 120 }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.termMonths}
              defaultValue={filter.termMonths}
              onChange={(event) => handleFilterChange(event, "termMonths")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "typeOfContainer",
      headerName: "Container Type",
      cellClassName: "status-badge",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      sortable: false, // disables sorting for this column
      minWidth: 120,
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 100 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.typeOfContainer}
              defaultValue={filter.typeOfContainer}
              onChange={(event) => handleFilterChange(event, "typeOfContainer")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "initialQuantity",
      headerName: "Initial Quantity",
      cellClassName: "status-badge",
      headerClassName: "table-header border-0 my-2 h-100",
      flex: 1,
      minWidth: 130,
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 120 }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.initialQuantity}
              defaultValue={filter.initialQuantity}
              onChange={(event) => handleFilterChange(event, "initialQuantity")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      cellClassName: "status-badge",
      headerClassName: "table-header border-0 my-2 h-100",
      sortable: false, // disables sorting for this column
      flex: 1,
      minWidth: 180,
      renderCell: (params: any) => (
        <div
          className="dropdown-nospacing"
          onClick={(e) => e.stopPropagation()}
        >
          <StatusDropdown
            currentStatus={formatStatus(params.row.status)}
            onStatusChange={(e) => console.log(e)}
            disableCondition={true}
            allowedLabels={[]}
          />
        </div>
      ),
      renderHeader: (params) => (
        <>
          <div>
            <Typography
              variant="body2"
              sx={{ color: "#6F6F6F", fontSize: "14px" }}
            >
              {params.colDef.headerName}
            </Typography>

            <div className="position-relative" style={{ width: "160px" }}>
              <div
                className="form-control mb-1"
                style={{
                  cursor: "pointer",
                  borderBottomColor: "#D1D1D1",
                  marginTop: "4px",
                  background: "#fff",
                  padding: "5px",
                  overflowX: "hidden",
                  paddingRight: "16px",
                  textOverflow: "ellipsis",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleDropdown();
                }}
                ref={buttonRef}
              >
                {filter.status?.length > 0
                  ? CommonService.formatStatus(filter.status).join(", ")
                  : "Select Status"}

                <img
                  src={ExpandMore}
                  className="position-absolute top-50 end-0 translate-middle"
                />
              </div>
              {/* Custom Dropdown */}
              {showDropdown && (
                <div
                  className="dropdown-menu show status-multiselect-dropdown"
                  onClick={(event) => event.stopPropagation()}
                  ref={divRef}
                >
                  {getStatusOptions().map((option) => (
                    <Form.Check
                      key={option.value}
                      id={`status-${option.value}`}
                      type="checkbox"
                      label={option.label}
                      value={option.value}
                      checked={
                        filter.status?.split(",").includes(option.value) ||
                        false
                      }
                      onChange={(event) => {
                        event.stopPropagation();
                        handleStatusFilterChange(
                          event,
                          option.value,
                          filter.status
                        );
                      }}
                      className="cursor-pointer"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ),
    },
    ...(CommonService.isAdmin()
      ? [
          {
            field: "action",
            headerName: "Action",
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: (params: any) => (
              <>
                {(CommonService.isAdmin() && params.row.status == Constant.ContractStatus.ACTIVE) && (
                    <div
                      className="d-flex align-items-center gap-2 h-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Amendment contract" arrow>
                        <IconButton
                          aria-label="message"
                          size="small"
                          onClick={() => handleEditContract(params?.row?.id)}
                          disabled={
                            params?.row?.roleName == "SUPER_ADMIN" &&
                            CommonService.isUser()
                          }
                        >
                          <img
                            src={EditIcon}
                            className="cursor-pointer"
                            alt="EditIcon"
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
              </>
            ),
          },
        ]
      : []),
  ];

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    fetchAllCompaniesContracts(
      newPaginationModel.page,
      newPaginationModel.pageSize,
      sortField,
      sortDirection,
      filter
    );
  };

  return (
    <div className={`usersList capcity-wrapper ${CommonService.isAdmin() ? "pb-5" : ""}`}>
      {CommonService.isAdmin() ? (
        <>
          <div className="usersList__header--wrapper d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <IconButton
                onClick={handleBack}  
                aria-label="ArrowBackIcon"
                sx={{
                  backgroundColor: "#FFF",
                  "&:hover": { backgroundColor: "#FFF" },
                }}
              >
                <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
              </IconButton>
              <div className="header--title">
                <h1 className="title mb-0">Contracts</h1>
              </div>
            <div className="divider mx-2"></div>
            <div className="barrel-sync-page-dropdown">
              <button
                className="primary-btn d-inline-flex"
                onClick={handleAddContract}
              >
                <AddSharpIcon />
                Add Contract
              </button>
            </div>
            </div>

            <div
              className="btn-group user-activity-btn-grp flex-wrap ms-auto"
              role="group"
              aria-label="Basic mixed styles example"
            >
              <button
                className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                  isContractOverviewClicked ? "bg-theme" : "bg-theme-white"
                }`}
                onClick={() => {
                  setCapacityInsightsClicked(false);
                  setContractOverviewClicked(true);
                }}
                style={{paddingBlock:"12px"}}
              >
               Contract Overview
              </button>

              <button
                className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                  isCapacityInsightsClicked ? "bg-theme" : "bg-theme-white"
                }`}
                style={{paddingBlock:"12px"}}
                onClick={() => {
                  setContractOverviewClicked(false);
                  setCapacityInsightsClicked(true);
                }}
              >
                Capacity Insights
                
              </button>
            </div>
            <div className="blank-div">

            </div>
          </div>
        </>
      ) : (
      <>
        <div className="usersList__header--wrapper d-flex flex-wrap justify-content-between align-items-center mb-3 border-bottom">
          <div className="d-flex align-items-center capacity-detail-tabbing">
            <button
              className={`btn d-flex align-items-center gap-2 ${
                isContractOverviewClicked ? 'active' : ''
              }`}
              onClick={() => {
                setContractOverviewClicked(true);
                setCapacityTabActive(false);
              }}
            >
               <span>

                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 4.125H3C2.70163 4.125 2.41548 4.24353 2.2045 4.4545C1.99353 4.66548 1.875 4.95163 1.875 5.25V18C1.875 18.4973 2.07254 18.9742 2.42417 19.3258C2.77581 19.6775 3.25272 19.875 3.75 19.875H20.25C20.7473 19.875 21.2242 19.6775 21.5758 19.3258C21.9275 18.9742 22.125 18.4973 22.125 18V5.25C22.125 4.95163 22.0065 4.66548 21.7955 4.4545C21.5845 4.24353 21.2984 4.125 21 4.125ZM4.125 10.875H7.125V13.125H4.125V10.875ZM9.375 10.875H19.875V13.125H9.375V10.875ZM19.875 6.375V8.625H4.125V6.375H19.875ZM4.125 15.375H7.125V17.625H4.125V15.375ZM9.375 17.625V15.375H19.875V17.625H9.375Z" fill={ isContractOverviewClicked ? '#333333' : '#737376'} />
                  </svg>

                </span>
                <span>Contract Overview</span>
            </button>
            <button
              className={`btn d-flex align-items-center gap-2 ${
                isCapacityTabActive ? 'active' : ''
              }`}
              onClick={handleCapacityInsightTabClick}
            >
                  <span>

                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.37502 10.9153C9.48906 10.8495 9.58375 10.7548 9.64958 10.6407C9.7154 10.5267 9.75005 10.3973 9.75002 10.2656V3.51562C9.74935 3.39605 9.7201 3.27837 9.66471 3.1724C9.60931 3.06643 9.52939 2.97523 9.43159 2.90643C9.33379 2.83762 9.22096 2.7932 9.10251 2.77686C8.98406 2.76052 8.86341 2.77274 8.75064 2.8125C6.56156 3.58726 4.72063 5.11748 3.55878 7.12807C2.39694 9.13865 1.99038 11.4977 2.4122 13.7812C2.43407 13.8993 2.48397 14.0104 2.55771 14.1052C2.63145 14.2 2.72687 14.2757 2.83595 14.3259C2.93425 14.3719 3.0415 14.3956 3.15002 14.3953C3.28165 14.3954 3.41098 14.3608 3.52502 14.295L9.37502 10.9153ZM8.25002 4.65188V9.8325L3.76127 12.4228C3.75002 12.2812 3.75002 12.1388 3.75002 12C3.75135 10.4797 4.17216 8.98928 4.96613 7.69279C5.76011 6.3963 6.89644 5.34404 8.25002 4.65188ZM12 2.25C11.8011 2.25 11.6103 2.32902 11.4697 2.46967C11.329 2.61032 11.25 2.80109 11.25 3V11.6081L3.86158 15.9122C3.77584 15.9619 3.7008 16.0281 3.6408 16.107C3.5808 16.1858 3.53703 16.2758 3.51201 16.3717C3.48699 16.4676 3.48122 16.5675 3.49504 16.6656C3.50886 16.7638 3.54199 16.8582 3.59252 16.9434C4.45849 18.4156 5.69599 19.6344 7.18113 20.478C8.66627 21.3215 10.3469 21.7601 12.0549 21.7498C13.7628 21.7396 15.4381 21.2809 16.913 20.4196C18.3879 19.5583 19.6107 18.3247 20.459 16.8422C21.3072 15.3598 21.7511 13.6805 21.7463 11.9726C21.7415 10.2646 21.2882 8.58787 20.4316 7.11022C19.575 5.63257 18.3453 4.40586 16.8656 3.55289C15.3858 2.69993 13.708 2.25064 12 2.25ZM12 20.25C10.6907 20.2465 9.40091 19.9331 8.23601 19.3354C7.07112 18.7377 6.06427 17.8727 5.29783 16.8113L12.3778 12.6863C12.4917 12.6205 12.5863 12.5259 12.6522 12.4121C12.718 12.2982 12.7527 12.169 12.7528 12.0375V3.78375C14.8682 3.97567 16.8281 4.9761 18.2244 6.57675C19.6207 8.1774 20.3458 10.2549 20.2488 12.3768C20.1518 14.4987 19.2401 16.5013 17.7036 17.9679C16.1671 19.4345 14.1241 20.2519 12 20.25Z" fill={ isCapacityTabActive ? '#333333' : '#737376'} />
                    </svg>

                  </span>
                  <span>Capacity Insights</span>
            </button>
          </div>  
        </div>
      </>
      )}

      {isContractOverviewClicked ? (
        <div className="user-table-container">
        <div className="userTable">
          <DataGrid
            rows={companiesContract}
            columns={columns}
            className="data-grid support-req--listing"
            initialState={{
              pagination: {
                paginationModel: { page, pageSize },
              },
            }}
            pagination
            rowCount={totalCount}
            paginationModel={{ page, pageSize }}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortChange}
            onPaginationModelChange={onPaginationModelChange}
            disableRowSelectionOnClick={true}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            slots={{
              loadingOverlay: DataGridLoader,
            }}
            disableColumnResize
            onRowClick={handleRowClick}
          />
        </div>
      </div>
      ): (
        isCapacityInsightsClicked ? (
        <CompanyContractCapacityListing/>
        ) : (
          <CapacityDetailsChart/>
        )
      )}
    </div>
  );
};

export default CompaniesContractListing;
